import {
  FaStar,
  FaCheck,
  FaXmark,
  FaSnowflake,
  FaBrain,
  FaCrown,
  FaCalendarCheck,
  FaFire,
  FaClock,
} from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { useTelegram } from "../../context/TelegramContext";
import { useState, useEffect, useCallback, useRef } from "react";
// import { Link } from "react-router-dom"; 
import { getPremium, postSubscribe } from "../../api/mainApi";

/* ──────────────────────────────────────────────────────────────────────────
   1. Shared Logic Layer
   ────────────────────────────────────────────────────────────────────────── */
export function usePremium() {
  const tg = useTelegram();
  const { i18n } = useTranslation();

  // Безпечно дістаємо статус преміуму
  const getInitialPremium = () => {
    const val = localStorage.getItem("premium");
    if (val === "true") return true;
    const num = parseInt(val, 10);
    if (!isNaN(num) && num > 0) return num;
    return false;
  };

  const [premium, setPremium] = useState(getInitialPremium());
  const [loadingData, setLoadingData] = useState(true);
  
  const [rawPlans, setRawPlans] = useState([]);
  const [globalDiscountEnd, setGlobalDiscountEnd] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  const [loadingPay, setLoadingPay] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  
  const [timeLeft, setTimeLeft] = useState(null);
  const [isDiscountActive, setIsDiscountActive] = useState(false);

  useEffect(() => {
    if (!tg?.initData) return;

    (async () => {
      try {
        setLoadingData(true);
        const data = await getPremium(); 
        
        setPremium(data.premium);
        localStorage.setItem("premium", data.premium);
        
        // Очікуємо discount_end_date на рівні з plans
        if (data.discount_end_date) {
          setGlobalDiscountEnd(data.discount_end_date);
        }
        
        let plansArray = [];
        if (data.plans) {
             plansArray = Array.isArray(data.plans) 
              ? data.plans 
              : Object.values(data.plans);
        }
        
        plansArray = plansArray.filter(plan => plan && plan.id);

        if (plansArray.length > 0) {
          const sortedPlans = plansArray.sort((a, b) => a.durationMonths - b.durationMonths);
          setRawPlans(sortedPlans);
          
          const popularPlan = sortedPlans.find(p => p.isPopular);
          setSelectedPlanId(popularPlan ? popularPlan.id : sortedPlans[0].id);
        }
      } catch (err) {
        console.error("getPremium failed:", err);
      } finally {
        setTimeout(() => setLoadingData(false), 500);
      }
    })();
  }, [tg]);

  useEffect(() => {
    const regDateStr = localStorage.getItem("reg_date");
    const regDate = regDateStr ? parseInt(regDateStr, 10) : 0;
    
    const individualDeadline = regDate ? regDate + 48 * 60 * 60 * 1000 : 0;
    const globalDeadline = globalDiscountEnd || 0;
    
    const updateTimer = () => {
      const now = Date.now();
      const hasIndividual = now < individualDeadline;
      const hasGlobal = now < globalDeadline;
      
      if (hasIndividual || hasGlobal) {
        setIsDiscountActive(true);
        const activeDeadline = (hasIndividual && hasGlobal) 
          ? Math.min(individualDeadline, globalDeadline) 
          : (hasIndividual ? individualDeadline : globalDeadline);

        const difference = activeDeadline - now;
        if (difference > 0) {
          const h = Math.floor((difference / (1000 * 60 * 60)));
          const m = Math.floor((difference / 1000 / 60) % 60);
          const s = Math.floor((difference / 1000) % 60);
          setTimeLeft(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`);
        } else {
          setTimeLeft(null);
          setIsDiscountActive(false);
        }
      } else {
        setTimeLeft(null);
        setIsDiscountActive(false);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [globalDiscountEnd]);

  const buyPremium = useCallback(async () => {
    if (!selectedPlanId || premium || loadingPay) return;
    setLoadingPay(true);

    try {
      const { invoiceLink } = await postSubscribe({ planId: selectedPlanId });
      tg.openInvoice(invoiceLink);
    } catch (err) {
      console.error("subscribe error:", err);
      tg.showAlert?.(`subscribe error: ${err.message}`);
    } finally {
      setLoadingPay(false);
    }
  }, [selectedPlanId, premium, loadingPay, tg]);

  useEffect(() => {
    if (!tg?.onEvent) return;
    const handler = ({ status }) => {
      if (status === "paid") {
        setShowSuccessScreen(true);
      } else if (status === "failed") {
        tg.showAlert?.("Оплата скасована або не пройшла 😔");
      }
    };
    tg.onEvent("invoiceClosed", handler);
    return () => tg.offEvent?.("invoiceClosed", handler);
  }, [tg]);

  const handleSuccessClose = () => {
    window.location.reload();
  };

  const formatExpirationDate = (days) => {
    if (!days) return "";
    const locale = i18n.language || "en-US";
    const d = new Date(Date.now() + days * 86_400_000);
    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(d);
  };

  return {
    premium,
    rawPlans,
    isDiscountActive,
    timeLeft,
    loadingPay,
    loadingData,
    buyPremium,
    formatExpirationDate,
    selectedPlanId,
    setSelectedPlanId,
    showSuccessScreen,
    handleSuccessClose,
  };
}

/* ──────────────────────────────────────────────────────────────────────────
   2. UI Components
   ────────────────────────────────────────────────────────────────────────── */
function SuccessScreen({ onClose }) {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950 flex flex-col items-center justify-center p-6 animate-fade-in text-center overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-yellow-400 rounded-full animate-ping"></div>
        <div className="absolute top-1/3 right-1/4 w-3 h-3 bg-cyan-400 rounded-full animate-bounce"></div>
        <div className="absolute bottom-1/3 left-1/3 w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-emerald-500/20 rounded-full blur-[100px]"></div>
      </div>

      <div className="relative z-10 flex flex-col items-center gap-6">
        <div className="w-28 h-28 bg-gradient-to-tr from-yellow-300 to-amber-500 rounded-full flex items-center justify-center shadow-[0_0_40px_rgba(251,191,36,0.4)] animate-scale-up">
          <FaCrown className="text-5xl text-white drop-shadow-md" />
        </div>

        <div className="space-y-2 animate-slide-up" style={{ animationDelay: "0.2s" }}>
          <h2 className="text-3xl font-black text-white uppercase tracking-wider">
            {t("premium.successTitle") || "You are Premium!"}
          </h2>
          <p className="text-slate-300 text-lg">
            {t("premium.successSubtitle") || "Всі можливості розблоковано."}
          </p>
        </div>

        <button
          onClick={onClose}
          className="mt-8 px-10 py-4 bg-white text-slate-900 font-bold rounded-full text-xl shadow-xl hover:scale-105 active:scale-95 transition-all animate-slide-up"
          style={{ animationDelay: "0.4s" }}
        >
          {t("premium.continue") || "Чудово!"}
        </button>
      </div>
    </div>
  );
}

function PremiumSkeleton() {
  return (
    <div className="animate-pulse flex flex-col flex-1 w-full bg-slate-950">
      <div className="w-full h-64 bg-slate-900/50 shrink-0" />
      <div className="flex flex-col flex-1 px-4 mt-6 space-y-8">
        <div className="space-y-3">
          <div className="h-3 w-32 bg-slate-800 rounded mx-2" />
          <div className="flex gap-3 overflow-hidden no-scrollbar px-1 pt-2">
            <div className="min-w-[140px] h-32 bg-slate-900 rounded-2xl border border-slate-800/50" />
            <div className="min-w-[140px] h-32 bg-slate-900 rounded-2xl border border-slate-800/50" />
            <div className="min-w-[140px] h-32 bg-slate-900 rounded-2xl border border-slate-800/50" />
          </div>
        </div>
        
        <div className="space-y-4 px-2">
          <div className="flex justify-between items-center mb-2 border-b border-slate-800 pb-2">
            <div className="h-3 w-24 bg-slate-800 rounded" />
            <div className="flex gap-6">
              <div className="h-3 w-8 bg-slate-800 rounded" />
              <div className="h-3 w-12 bg-slate-800 rounded" />
            </div>
          </div>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex justify-between items-center py-3 border-b border-white/5">
              <div className="space-y-2 w-1/2">
                <div className="h-3 w-full bg-slate-800 rounded" />
                {i % 2 === 0 && <div className="h-2 w-2/3 bg-slate-900 rounded" />}
              </div>
              <div className="flex gap-10 mr-2">
                <div className="h-4 w-4 bg-slate-800 rounded" />
                <div className="h-4 w-4 bg-slate-800 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Скелетон нижньої приклеєної кнопки */}
      <div className="sticky bottom-0 left-0 right-0 bg-slate-950 p-4 pt-6 mt-auto z-50">
        <div className="absolute bottom-full left-0 right-0 h-24 bg-gradient-to-t from-slate-950 to-transparent"></div>
        <div className="h-14 w-full bg-slate-800 rounded-2xl" />
        <div className="h-2 w-32 bg-slate-900 rounded mx-auto mt-4" />
      </div>
    </div>
  );
}

function PlanSelector({ plans, selectedId, onSelect, premiumActive }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (containerRef.current && selectedId) {
      const selectedEl = containerRef.current.querySelector(`[data-id="${selectedId}"]`);
      if (selectedEl) {
        selectedEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [selectedId, plans.length]);

  if (premiumActive || plans.length === 0) return null;

  return (
    <div ref={containerRef} className="flex gap-3 overflow-x-auto pb-4 pt-6 px-4 no-scrollbar snap-x">
      {plans.map((plan) => {
        const isSelected = selectedId === plan.id;
        const discountPercent = plan.displayOldPrice 
          ? Math.round((1 - plan.currentPrice / plan.displayOldPrice) * 100) 
          : null;

        return (
          <div
            key={plan.id}
            data-id={plan.id}
            onClick={() => onSelect(plan.id)}
            className={`
              relative min-w-[140px] flex-1 cursor-pointer snap-center
              flex flex-col items-center justify-center p-4 rounded-2xl border-2 transition-all duration-300
              ${
                isSelected
                  ? "bg-slate-800/80 border-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.3)] scale-105"
                  : "bg-slate-900/50 border-white/10 opacity-70 hover:opacity-100"
              }
            `}
          >
            {plan.isPopular && (
              <div className="absolute -top-3.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full shadow-sm flex items-center gap-1 z-10">
                <FaFire className="text-[8px]" /> POPULAR
              </div>
            )}
            
            {discountPercent > 0 && !plan.isPopular && (
              <div className="absolute -top-3 bg-gradient-to-r from-pink-500 to-rose-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm z-10">
                -{discountPercent}%
              </div>
            )}

            <p className="text-slate-400 text-xs font-medium uppercase tracking-wider mb-2">
              {plan.durationMonths} Month{plan.durationMonths > 1 ? 's' : ''}
            </p>
            
            <div className="flex items-center justify-center gap-1.5 h-8">
              <span className="text-2xl font-bold text-white leading-none">
                {plan.currentPrice}
              </span>
              <FaStar className="text-white text-sm shrink-0" />
            </div>
            
            <div className="h-4 mt-1">
                {plan.displayOldPrice && (
                <span className="text-xs text-slate-500 line-through flex items-center justify-center gap-1">
                    {plan.displayOldPrice} <FaStar className="text-[8px]" />
                </span>
                )}
            </div>

            {isSelected && (
              <div className="absolute -bottom-3 bg-cyan-500 rounded-full p-1 border-4 border-slate-950">
                <FaCheck className="text-white text-xs" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function FeatureRow({ label, sub, basic, premium, isLast }) {
  return (
    <div className={`flex items-center py-4 ${!isLast ? "border-b border-white/5" : ""}`}>
      <div className="flex-1 pr-2">
        <p className="text-sm text-slate-200 font-medium leading-tight">{label}</p>
        {sub && <p className="text-xs text-slate-500 mt-1 leading-tight">{sub}</p>}
      </div>
      <div className="w-16 flex flex-col items-center justify-center opacity-40 grayscale">
        <div className="text-sm font-semibold text-slate-300 text-center">{basic}</div>
      </div>
      <div className="w-20 flex flex-col items-center justify-center relative">
        <div className="absolute inset-0 bg-emerald-500/5 rounded-lg -z-10 blur-sm"></div>
        <div className="text-base font-bold text-emerald-400 drop-shadow-sm text-center">
          {premium}
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
   3. Main Component
   ────────────────────────────────────────────────────────────────────────── */
export function PremiumUI({ variant = "page", show = true, onClose }) {
  const { t, i18n } = useTranslation();
  const {
    premium,
    rawPlans,
    isDiscountActive,
    timeLeft,
    loadingPay,
    loadingData,
    buyPremium,
    formatExpirationDate,
    selectedPlanId,
    setSelectedPlanId,
    showSuccessScreen,
    handleSuccessClose,
  } = usePremium();

  // Логіка цін на основі твоєї БД (де price - акційна, oldPrice - звичайна).
  const displayPlans = rawPlans.map(p => {
    const hasDiscountInDB = p.oldPrice && p.oldPrice > p.price;
    return {
      ...p,
      currentPrice: (isDiscountActive && hasDiscountInDB) ? p.price : (p.oldPrice || p.price),
      displayOldPrice: (isDiscountActive && hasDiscountInDB) ? p.oldPrice : null
    }
  });

  const baseFeatures = [
    {
      label: t("premium.features.moods"),
      basic: "5",
      premium: "15",
    },
    {
      label: t("premium.features.models"),
      basic: "1",
      premium: "3",
    },
    {
      label: t("premium.features.freezes"),
      sub: t("premium.features.freezesSub"),
      basic: <span className="flex items-center gap-1">3 <FaSnowflake className="text-xs" /></span>,
      premium: <span className="flex items-center gap-1">5 <FaSnowflake className="text-xs" /></span>,
    },
    {
      label: t("premium.features.points"),
      sub: t("premium.features.pointsSub"),
      basic: <span className="flex items-center gap-1">100 <FaBrain className="text-xs" /></span>,
      premium: <span className="flex items-center gap-1">500 <FaBrain className="text-xs" /></span>,
    },
    {
      label: t("premium.features.breathing"),
      basic: t("premium.features.basic_amount") || "База",
      premium: t("premium.features.more_amount") || "Більше",
    },
    {
      label: t("premium.features.sounds"),
      basic: t("premium.features.basic_amount") || "База",
      premium: t("premium.features.more_amount") || "Більше",
    },
    {
      label: t("premium.features.stats"),
      basic: <FaCheck />,
      premium: <FaCheck />,
    },
    {
      label: t("premium.features.adfree"),
      basic: <FaXmark />,
      premium: <FaCheck />,
    },
  ];

  const features = [...baseFeatures];
  if (i18n.language && i18n.language.startsWith("uk")) {
    features.splice(5, 0, {
      label: t("premium.features.meditations"),
      basic: t("premium.features.basic_amount") || "База",
      premium: t("premium.features.more_amount") || "Більше",
    });
  }

  const selectedPlan = displayPlans.find(p => p.id === selectedPlanId);
  const currentPrice = selectedPlan ? selectedPlan.currentPrice : "...";
  const displayOldPrice = selectedPlan ? selectedPlan.displayOldPrice : null;

  const isModal = variant === "modal";
  if (isModal && !show) return null;
  if (showSuccessScreen) return <SuccessScreen onClose={handleSuccessClose} />;

  const Wrapper = isModal ? "div" : "div";
  const wrapperClasses = isModal
    ? "fixed inset-0 z-[100] flex items-end justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
    : "h-dvh w-full bg-slate-950 text-white overflow-hidden";

  const containerClasses = isModal
    ? "w-full max-w-md bg-slate-900 rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[90vh] flex flex-col relative animate-slide-up"
    : "relative w-full max-w-lg mx-auto flex flex-col h-full";

  const backButtonClasses = isModal
    ? "absolute top-4 right-4 z-50 w-8 h-8 flex items-center justify-center bg-black/40 backdrop-blur-md rounded-full text-white hover:bg-black/60 transition shadow-lg"
    : "absolute top-4 left-4 z-50 w-10 h-10 flex items-center justify-center bg-black/40 backdrop-blur-md rounded-full text-white hover:bg-black/60 transition shadow-lg";

  return (
    <Wrapper className={wrapperClasses}>
      <div className={containerClasses}>
        {isModal && (
          <button onClick={onClose} className={backButtonClasses}>
            <FaXmark className="text-xl" />
          </button>
        )}

        {/* ОСНОВНИЙ КОНТЕЙНЕР ДЛЯ СКРОЛУ. У ньому лежить усе: і контент, і приклеєна кнопка */}
        <div className="flex-1 overflow-y-auto no-scrollbar flex flex-col relative w-full">
          
          <div className="relative w-full shrink-0">
            {!loadingData && (
              <img
                src="/Premium-large.webp"
                alt="Premium Banner"
                className="w-full h-auto block object-cover"
              />
            )}
          </div>

          {loadingData ? (
            <PremiumSkeleton />
          ) : (
            <div className="flex flex-col flex-1 z-20 relative bg-slate-950 shadow-[0_-5px_20px_rgba(0,0,0,0.5)]">
              
              {!premium && isDiscountActive && timeLeft ? (
                <div className="bg-gradient-to-r from-amber-600/20 to-orange-600/20 py-3 px-5 flex justify-between items-center border-b border-amber-500/20 shadow-inner">
                  <p className="text-base text-amber-200 font-semibold tracking-wide">
                    {t("premium.specialOffer", "Спеціальна пропозиція")}:
                  </p>
                  <div className="flex items-center gap-1.5 bg-amber-500/20 px-3 py-1.5 rounded-lg text-amber-400 text-sm font-mono font-bold shadow-sm border border-amber-500/10">
                    <FaClock /> {timeLeft}
                  </div>
                </div>
              ) : null}

              {premium ? (
                <div className="px-4 pt-6 pb-2">
                  <div className="bg-gradient-to-r from-emerald-900/40 to-cyan-900/40 border border-emerald-500/30 rounded-2xl p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
                      <FaCalendarCheck className="text-emerald-400 text-xl" />
                    </div>
                    <div>
                      <h3 className="font-bold text-emerald-400 text-lg">
                        {premium === true ? t("premium.lifetime") : "Premium Active"}
                      </h3>
                      {premium !== true && (
                        <p className="text-sm text-slate-400">
                          {t("premium.validUntil")}:{" "}
                          <span className="text-white">{formatExpirationDate(premium)}</span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}

              {!premium && (
                <div className="mt-6">
                  <h3 className="px-5 text-sm font-bold text-slate-400 uppercase tracking-wider mb-1">
                    {t("premium.availablePlans") || "Available Plans"}
                  </h3>
                  <PlanSelector
                    plans={displayPlans}
                    selectedId={selectedPlanId}
                    onSelect={setSelectedPlanId}
                    premiumActive={premium}
                  />
                </div>
              )}

              <div className="px-5 pt-4 pb-2 mt-2">
                <div className="flex items-center text-xs uppercase font-bold tracking-widest text-slate-500 mb-2">
                  <span className="flex-1">{t("premium.featuresTitle") || "Benefits"}</span>
                  <span className="w-16 text-center">Basic</span>
                  <span className="w-20 text-center text-emerald-500">Premium</span>
                </div>
                <div className="h-px bg-gradient-to-r from-transparent via-slate-800 to-transparent"></div>
              </div>

              {/* Збільшили відступ pb-6 на pb-20, щоб елементи гарантовано заходили під градієнт */}
              <div className="px-5 pb-20 relative">
                {features.map((feat, i) => (
                  <FeatureRow key={i} {...feat} isLast={i === features.length - 1} />
                ))}
              </div>
            </div>
          )}
          
          {/* STICKY CTA БЛОК. Завжди приклеєний до низу контейнера */}
          {!loadingData && (
            <div className="sticky bottom-0 left-0 right-0 z-50 bg-slate-950 pb-6 pt-4 px-4 sm:rounded-b-3xl mt-auto">
              
              {/* Градієнт над кнопкою. Збільшено висоту до h-24 і додано via-slate-950/80 для плавного і сильного розмиття тексту під ним */}
              <div className="absolute bottom-full left-0 right-0 h-24 bg-gradient-to-t from-slate-950 via-slate-950/80 to-transparent pointer-events-none"></div>
              
              <button
                onClick={buyPremium}
                disabled={loadingPay || premium || !selectedPlanId}
                className={`
                  relative w-full py-4 rounded-2xl font-bold text-lg shadow-[0_0_20px_rgba(16,185,129,0.3)] flex items-center justify-center gap-2 overflow-hidden group transition-all
                  ${
                    premium
                      ? "bg-slate-800 text-slate-400 shadow-none cursor-default"
                      : "bg-gradient-to-r from-emerald-500 via-cyan-500 to-emerald-500 bg-[length:200%_auto] animate-gradient-move text-white active:scale-95"
                  }
                `}
              >
                {!premium && (
                  <div className="absolute inset-0 -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent z-10" />
                )}

                {loadingPay ? (
                  <>
                    <FaSnowflake className="animate-spin text-xl" />
                    <span>{t("premium.loading")}</span>
                  </>
                ) : premium ? (
                  <>
                    <FaCheck /> {t("premium.already")}
                  </>
                ) : (
                  <>
                    {/* Виведення старої (закресленої) ціни + звичайної кнопки */}
                    <div className="flex items-center gap-2">
                      <FaStar className="text-white" />
                      <div className="flex items-center gap-2">
                        {displayOldPrice && (
                          <span className="text-white/60 line-through text-sm font-medium flex items-center gap-0.5">
                            {displayOldPrice} <FaStar className="text-[10px]" />
                          </span>
                        )}
                        <span>{t("premium.button", { price: currentPrice })}</span>
                      </div>
                    </div>
                  </>
                )}
              </button>

              {!premium && (
                <p className="text-center text-xs text-slate-500 mt-3 flex items-center justify-center gap-1 relative z-10">
                  <FaCheck className="text-[10px]" />{" "}
                  {t("premium.secure_payment") || "Secured by Telegram"}
                </p>
              )}
            </div>
          )}

        </div>
      </div>
    </Wrapper>
  );
}